
import { useEffect } from 'react';
import { useCompare } from '../context/CompareContext';
import { useCart } from '../context/CartContext';

export default function CompareModal() {
  const { compareItems, isCompareOpen, closeCompare, removeFromCompare, clearCompare } = useCompare();
  const { addToCart } = useCart();

  useEffect(() => {
    if (isCompareOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isCompareOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCompare();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [closeCompare]);

  const handleAddToCart = (item: typeof compareItems[0]) => {
    addToCart({
      id: `kit-${item.id}`,
      name: item.name,
      price: item.price,
      originalPrice: item.originalPrice,
      image: item.image,
      type: 'kit',
      description: item.description,
    });
  };

  if (!isCompareOpen) return null;

  const comparisonFeatures = [
    { key: 'price', label: 'Price', render: (item: typeof compareItems[0]) => (
      <div>
        <span className="text-2xl font-display text-stone-900">${item.price}</span>
        <span className="text-stone-400 line-through text-sm ml-2">${item.originalPrice}</span>
      </div>
    )},
    { key: 'savings', label: 'You Save', render: (item: typeof compareItems[0]) => (
      <span className="text-emerald-600 font-semibold">${item.originalPrice - item.price} ({Math.round((1 - item.price / item.originalPrice) * 100)}% off)</span>
    )},
    { key: 'rating', label: 'Rating', render: (item: typeof compareItems[0]) => (
      <div className="flex items-center gap-2">
        <div className="flex">
          {[...Array(5)].map((_, i) => (
            <i key={i} className={`ri-star-fill text-sm ${i < Math.floor(item.rating) ? 'text-amber-400' : 'text-stone-200'}`}></i>
          ))}
        </div>
        <span className="text-stone-600 font-medium">{item.rating}</span>
        <span className="text-stone-400 text-sm">({item.reviews})</span>
      </div>
    )},
    { key: 'tools', label: 'Tools Included', render: (item: typeof compareItems[0]) => (
      <div className="flex items-center gap-2">
        <i className="ri-tools-line text-amber-500"></i>
        <span className="font-medium text-stone-700">{item.tools} pieces</span>
      </div>
    )},
    { key: 'ingredients', label: 'Ingredients', render: (item: typeof compareItems[0]) => (
      <div className="flex items-center gap-2">
        <i className="ri-flask-line text-amber-500"></i>
        <span className="font-medium text-stone-700">{item.ingredients} items</span>
      </div>
    )},
    { key: 'totalItems', label: 'Total Items', render: (item: typeof compareItems[0]) => (
      <span className="font-semibold text-stone-900">{item.tools + item.ingredients} items</span>
    )},
    { key: 'valuePerItem', label: 'Value per Item', render: (item: typeof compareItems[0]) => (
      <span className="text-stone-600">${(item.price / (item.tools + item.ingredients)).toFixed(2)}/item</span>
    )},
    { key: 'skillLevel', label: 'Skill Level', render: (item: typeof compareItems[0]) => {
      const level = item.badge === 'Advanced' ? 'Advanced' : item.badge === 'Best Seller' ? 'Beginner' : 'Intermediate';
      const color = level === 'Beginner' ? 'bg-emerald-100 text-emerald-700' : level === 'Intermediate' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700';
      return <span className={`px-3 py-1 rounded-full text-xs font-semibold ${color}`}>{level}</span>;
    }},
    { key: 'shipping', label: 'Shipping', render: () => (
      <span className="text-emerald-600 flex items-center gap-1.5">
        <i className="ri-truck-line"></i>
        Free Shipping
      </span>
    )},
    { key: 'guarantee', label: 'Guarantee', render: () => (
      <span className="text-stone-600 flex items-center gap-1.5">
        <i className="ri-shield-check-line text-amber-500"></i>
        30-day return
      </span>
    )},
  ];

  const getBestValue = (key: string) => {
    if (compareItems.length < 2) return -1;
    
    switch (key) {
      case 'price':
        return compareItems.indexOf(compareItems.reduce((a, b) => a.price < b.price ? a : b));
      case 'savings':
        return compareItems.indexOf(compareItems.reduce((a, b) => 
          (a.originalPrice - a.price) > (b.originalPrice - b.price) ? a : b
        ));
      case 'rating':
        return compareItems.indexOf(compareItems.reduce((a, b) => a.rating > b.rating ? a : b));
      case 'tools':
        return compareItems.indexOf(compareItems.reduce((a, b) => a.tools > b.tools ? a : b));
      case 'ingredients':
        return compareItems.indexOf(compareItems.reduce((a, b) => a.ingredients > b.ingredients ? a : b));
      case 'totalItems':
        return compareItems.indexOf(compareItems.reduce((a, b) => 
          (a.tools + a.ingredients) > (b.tools + b.ingredients) ? a : b
        ));
      case 'valuePerItem':
        return compareItems.indexOf(compareItems.reduce((a, b) => 
          (a.price / (a.tools + a.ingredients)) < (b.price / (b.tools + b.ingredients)) ? a : b
        ));
      default:
        return -1;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm"
        onClick={closeCompare}
      ></div>

      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden mx-4 animate-in fade-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-stone-100 px-8 py-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-display text-stone-900">Compare Kits</h2>
            <p className="text-stone-500 text-sm mt-1">
              {compareItems.length === 0 
                ? 'Add kits to compare' 
                : `Comparing ${compareItems.length} kit${compareItems.length > 1 ? 's' : ''}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {compareItems.length > 0 && (
              <button
                onClick={clearCompare}
                className="px-4 py-2 text-stone-500 hover:text-stone-700 text-sm font-medium transition-colors cursor-pointer"
              >
                Clear All
              </button>
            )}
            <button
              onClick={closeCompare}
              className="w-10 h-10 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl text-stone-600"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-auto max-h-[calc(90vh-88px)]">
          {compareItems.length === 0 ? (
            <div className="p-16 text-center">
              <div className="w-20 h-20 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-6">
                <i className="ri-scales-3-line text-4xl text-stone-400"></i>
              </div>
              <h3 className="text-xl font-serif text-stone-900 mb-2">No kits to compare</h3>
              <p className="text-stone-500 mb-6">Add kits by clicking the compare button on any kit card</p>
              <button
                onClick={closeCompare}
                className="px-6 py-3 bg-stone-900 text-white rounded-full font-medium hover:bg-stone-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                Browse Kits
              </button>
            </div>
          ) : (
            <div className="p-8">
              {/* Product Headers */}
              <div className="grid gap-6" style={{ gridTemplateColumns: `200px repeat(${compareItems.length}, 1fr)` }}>
                <div></div>
                {compareItems.map((item) => (
                  <div key={item.id} className="text-center relative group">
                    <button
                      onClick={() => removeFromCompare(item.id)}
                      className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-stone-100 hover:bg-rose-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all cursor-pointer z-10"
                    >
                      <i className="ri-close-line text-stone-500 hover:text-rose-500"></i>
                    </button>
                    <div className="relative bg-gradient-to-br from-amber-50 to-stone-50 rounded-2xl p-6 mb-4">
                      <div className="absolute top-3 left-3 px-3 py-1 bg-stone-900 text-white text-[10px] font-semibold rounded-full">
                        {item.badge}
                      </div>
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-40 object-contain"
                      />
                    </div>
                    <h3 className="font-serif font-semibold text-stone-900 mb-2">{item.name}</h3>
                    <p className="text-stone-500 text-sm line-clamp-2">{item.description}</p>
                  </div>
                ))}
              </div>

              {/* Comparison Table */}
              <div className="mt-8 border-t border-stone-100">
                {comparisonFeatures.map((feature, idx) => {
                  const bestIdx = getBestValue(feature.key);
                  return (
                    <div
                      key={feature.key}
                      className={`grid gap-6 py-5 ${idx !== comparisonFeatures.length - 1 ? 'border-b border-stone-100' : ''}`}
                      style={{ gridTemplateColumns: `200px repeat(${compareItems.length}, 1fr)` }}
                    >
                      <div className="flex items-center">
                        <span className="text-stone-600 font-medium">{feature.label}</span>
                      </div>
                      {compareItems.map((item, itemIdx) => (
                        <div 
                          key={item.id} 
                          className={`flex items-center justify-center ${bestIdx === itemIdx ? 'relative' : ''}`}
                        >
                          {bestIdx === itemIdx && (
                            <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-1 h-8 bg-emerald-400 rounded-full"></div>
                          )}
                          {feature.render(item)}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>

              {/* Add to Cart Buttons */}
              <div 
                className="grid gap-6 mt-8 pt-8 border-t border-stone-200"
                style={{ gridTemplateColumns: `200px repeat(${compareItems.length}, 1fr)` }}
              >
                <div></div>
                {compareItems.map((item) => (
                  <div key={item.id} className="flex justify-center">
                    <button
                      onClick={() => handleAddToCart(item)}
                      className="px-8 py-3.5 bg-stone-900 text-white font-semibold rounded-full hover:bg-gradient-to-r hover:from-amber-400 hover:to-yellow-300 hover:text-stone-900 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
                    >
                      <i className="ri-shopping-cart-line"></i>
                      Add to Cart
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
